/**
 * .909 Kto mi práve volá.
 *
 * Oto: „CRM ukazovateľ telefonátu – kto mi práve volá."
 *
 *  - Keď mobil (MacroDroid) alebo ústredňa pošle číslo, všetkým prihláseným
 *    vyskočí vpravo dole okienko „Volá …" – meno/firma, objednávky z e-shopu,
 *    objednávky CRM, reklamácie, leady. Klik na objednávku e-shopu otvorí jej
 *    detail (aj s tlačidlom Telefonát na zápis hovoru).
 *  - Tlačidlo so slúchadlom v lište dole v menu: „Kto volá?" – číslo sa dá
 *    napísať aj ručne. Administrátor tam nájde napojenie mobilu.
 *  - Odkaz z upozornenia v telefóne (`/?volanie=0905…`) otvorí to isté okno.
 */
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Phone, PhoneIncoming, X, Copy, RefreshCw, Smartphone } from 'lucide-react';
import { api, money } from '../api';
import { Modal, useToast } from './ui';

const DetailObjednavkySklad = lazy(() => import('../pages/EshopSklad').then((m) => ({ default: m.DetailObjednavkySklad })));
const datum = (t) => (t ? String(t).slice(0, 10).split('-').reverse().join('.') : '');

function Karta({ d, onOtvorEshop, onZavri, kompakt = false }) {
  const kto = d.firma || d.meno;
  return (
    <div className="space-y-2 text-sm">
      <div className="flex items-start gap-2">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white"><PhoneIncoming size={20} /></div>
        <div className="min-w-0 flex-1">
          <div className="text-lg font-bold leading-tight text-slate-900">{kto || 'Neznáme číslo'}</div>
          {d.firma && d.meno && d.meno !== d.firma && <div className="text-slate-600">{d.meno}</div>}
          <a href={`tel:${d.cislo}`} className="font-mono text-base font-semibold text-blue-700">{d.cislo}</a>
        </div>
        {onZavri && <button className="rounded-lg p-1 text-slate-400 hover:bg-slate-100" onClick={onZavri} title="Zavrieť"><X size={18} /></button>}
      </div>
      {!d.poznany && <div className="rounded-lg bg-slate-50 p-2 text-slate-600">Toto číslo v CRM nemáme – nový zákazník?</div>}
      {d.eshop?.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Objednávky e-shop</div>
          {d.eshop.slice(0, kompakt ? 3 : 6).map((o) => (
            <button key={o.code} onClick={() => onOtvorEshop?.(o.code)}
              className="flex w-full items-center gap-2 rounded-lg px-1.5 py-1 text-left hover:bg-emerald-50">
              <span className="font-mono font-semibold text-slate-900">{o.code}</span>
              <span className="truncate rounded bg-slate-800 px-1.5 text-[11px] font-bold uppercase text-white">{o.status_name}</span>
              <span className="ml-auto whitespace-nowrap text-xs text-slate-500">{datum(o.created_time)} · {money(o.total_gross)}</span>
            </button>
          ))}
        </div>
      )}
      {d.objednavky?.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Objednávky CRM</div>
          {d.objednavky.slice(0, kompakt ? 2 : 6).map((o) => (
            <Link key={o.id} to={`/objednavky/${o.id}`} onClick={onZavri} className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50">
              <span className="font-mono font-semibold">{o.number}</span><span className="text-xs text-slate-500">{o.status}</span>
              <span className="ml-auto text-xs text-slate-500">{datum(o.order_date)} · {money(o.total_gross)}</span>
            </Link>
          ))}
        </div>
      )}
      {d.reklamacie?.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Reklamácie a servis</div>
          {d.reklamacie.slice(0, 3).map((c) => (
            <Link key={c.id} to={`/servis/${c.id}`} onClick={onZavri} className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50">
              <span className="font-mono font-semibold">{c.claim_no}</span><span className="text-xs text-slate-500">{c.status}</span>
              <span className="ml-auto text-xs text-slate-500">{datum(c.received_at)}</span>
            </Link>
          ))}
        </div>
      )}
      {d.leady?.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Leady</div>
          {d.leady.map((l) => (
            <Link key={l.id} to={`/leady/${l.id}`} onClick={onZavri} className="flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-slate-50">
              <span className="font-medium">{l.company || l.name}</span><span className="text-xs text-slate-500">{l.status}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Napojenie() {
  const toast = useToast();
  const [t, setT] = useState(null);
  const [cislo, setCislo] = useState('');
  useEffect(() => { api.get('/api/volanie/napojenie').then((v) => setT(v.token)).catch(() => {}); }, []);
  if (!t) return null;
  const url = `${window.location.origin}/api/volanie/hook/${t}?cislo=`;
  const kopiruj = async () => { try { await navigator.clipboard.writeText(url); toast.success('Adresa skopírovaná.'); } catch { toast.error('Skopíruj ručne.'); } };
  return (
    <details className="rounded-xl bg-slate-50 p-3 text-sm ring-1 ring-slate-200">
      <summary className="flex cursor-pointer items-center gap-2 font-medium text-slate-800"><Smartphone size={16} /> Napojenie mobilu (len administrátor)</summary>
      <ol className="mt-2 list-decimal space-y-1 pl-5 text-slate-700">
        <li>Na firemný Android nainštaluj <b>MacroDroid</b> (Google Play, zdarma).</li>
        <li>Nové makro → Spúšťač <b>Hovor → Prichádzajúci hovor</b> (akékoľvek číslo).</li>
        <li>Akcia <b>Pripojenie → HTTP požiadavka</b>, metóda GET, adresa nižšie a na koniec vlož premennú <b>[call_number]</b> (číslo volajúceho).</li>
        <li>Ulož makro. Pri zvonení sa číslo objaví v CRM u všetkých prihlásených.</li>
      </ol>
      <div className="mt-2 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 text-xs ring-1 ring-slate-200">{url}[call_number]</code>
        <button className="btn-secondary !py-1 !text-xs" onClick={kopiruj}><Copy size={14} /> Kopírovať</button>
      </div>
      <div className="mt-1 text-xs text-slate-500">Adresa obsahuje tajný kľúč – nikomu ju neposielaj. Ústredňa alebo VoIP, ktorá vie volať webhook, môže použiť tú istú adresu (parameter <code>cislo</code>).</div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input className="input w-44" placeholder="číslo na skúšku" value={cislo} onChange={(e) => setCislo(e.target.value)} />
        <button className="btn-secondary !py-1 !text-xs" onClick={async () => {
          try { await api.post('/api/volanie/skuska', { cislo }); toast.success('Skúšobný hovor odoslaný – o pár sekúnd vyskočí okienko.'); } catch (e) { toast.error(e.message); }
        }}><PhoneIncoming size={14} /> Skúšobný hovor</button>
        <button className="btn-ghost ml-auto !py-1 !text-xs text-red-600" onClick={async () => {
          if (!window.confirm('Vyrobiť nový kľúč? Starú adresu v mobile bude treba prepísať.')) return;
          try { setT((await api.post('/api/volanie/napojenie/novy', {})).token); toast.success('Nový kľúč.'); } catch (e) { toast.error(e.message); }
        }}><RefreshCw size={14} /> Nový kľúč</button>
      </div>
    </details>
  );
}

/** Okno „Kto volá?" – ručné hľadanie + história + napojenie. */
export function KtoVolaOkno({ open, onClose, user, predvolene = '', onOtvorEshop }) {
  const [cislo, setCislo] = useState(predvolene);
  const [d, setD] = useState(null);
  const [hist, setHist] = useState([]);
  useEffect(() => { if (open) { setCislo(predvolene); setD(null); api.get('/api/volanie/historia?limit=15').then((v) => setHist(v.hovory || [])).catch(() => {}); } }, [open, predvolene]);
  useEffect(() => {
    const c = cislo.replace(/\D/g, '');
    if (c.length < 9) { setD(null); return undefined; }
    const t = setTimeout(() => api.get(`/api/volanie/kto?cislo=${encodeURIComponent(cislo)}`).then(setD).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [cislo]);
  if (!open) return null;
  return (
    <Modal open onClose={onClose} title="Kto volá?" footer={<button className="btn-primary" onClick={onClose}>Zavrieť</button>}>
      <div className="space-y-3">
        <input className="input w-full text-lg" autoFocus inputMode="tel" placeholder="Telefónne číslo, napr. 0905 123 456"
          value={cislo} onChange={(e) => setCislo(e.target.value)} />
        {d && <div className="rounded-xl p-3 ring-1 ring-slate-200"><Karta d={d} onOtvorEshop={(k) => { onClose(); onOtvorEshop?.(k); }} onZavri={null} /></div>}
        {hist.length > 0 && (
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Posledné hovory</div>
            <div className="max-h-48 overflow-y-auto text-sm">
              {hist.map((h) => (
                <button key={h.id} onClick={() => setCislo(h.cislo)} className="flex w-full items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-slate-50">
                  <Phone size={13} className="text-slate-400" /><span className="font-mono">{h.cislo}</span>
                  <span className="truncate text-slate-600">{h.meno || '—'}</span>
                  <span className="ml-auto whitespace-nowrap text-xs text-slate-400">{new Date(`${h.kedy.replace(' ', 'T')}Z`).toLocaleString('sk-SK', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {user?.role === 'admin' && <Napojenie />}
      </div>
    </Modal>
  );
}

/** Okienko pri prichádzajúcom hovore + tlačidlo do lišty. */
export default function KtoVola({ user }) {
  const [hovory, setHovory] = useState([]);
  const [okno, setOkno] = useState(false);
  const [predvolene, setPredvolene] = useState('');
  const [eshopKod, setEshopKod] = useState(null);
  const po = useRef(null);
  const loc = useLocation();
  const nav = useNavigate();
  const smie = user && ['admin', 'user'].includes(user.role);

  /* odkaz z upozornenia: /?volanie=0905… */
  useEffect(() => {
    const p = new URLSearchParams(loc.search);
    const v = p.get('volanie');
    if (v) {
      setPredvolene(v); setOkno(true);
      p.delete('volanie');
      nav({ pathname: loc.pathname, search: p.toString() ? `?${p}` : '' }, { replace: true });
    }
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!smie) return undefined;
    let zrusene = false;
    const tik = async () => {
      if (document.hidden) return;
      try {
        const v = await api.get(`/api/volanie/nove?po=${po.current ?? 0}`);
        if (zrusene) return;
        if (po.current === null) { po.current = v.posledne; return; }     // staré neukazovať
        if (v.hovory?.length) {
          po.current = Math.max(po.current, ...v.hovory.map((h) => h.id));
          setHovory((x) => [...v.hovory, ...x].slice(0, 3));
          try {
            const ac = new (window.AudioContext || window.webkitAudioContext)();
            [0, 0.35].forEach((t0) => { const o = ac.createOscillator(); const g = ac.createGain(); o.frequency.value = 880; g.gain.value = 0.08; o.connect(g); g.connect(ac.destination); o.start(ac.currentTime + t0); o.stop(ac.currentTime + t0 + 0.18); });
          } catch { /* zvuk nie je dôležitý */ }
        } else po.current = Math.max(po.current, v.posledne || 0);
      } catch { /* ticho */ }
    };
    tik();
    const t = setInterval(tik, 4000);
    return () => { zrusene = true; clearInterval(t); };
  }, [smie]);

  /* tlačidlo „Kto volá?" v lište menu */
  useEffect(() => {
    const f = () => { setPredvolene(''); setOkno(true); };
    window.addEventListener('kto-vola', f);
    return () => window.removeEventListener('kto-vola', f);
  }, []);

  /* okienko samo zmizne po 2 minútach */
  useEffect(() => {
    if (!hovory.length) return undefined;
    const t = setTimeout(() => setHovory((x) => x.slice(0, -1)), 120000);
    return () => clearTimeout(t);
  }, [hovory]);

  if (!smie) return null;
  return (
    <>
      {hovory.length > 0 && (
        <div className="fixed bottom-24 right-4 z-[60] w-[360px] max-w-[calc(100vw-2rem)] space-y-2">
          {hovory.map((h) => (
            <div key={h.id} className="animate-[pulse_1s_ease-in-out_2] rounded-2xl bg-white p-3 shadow-2xl ring-2 ring-emerald-500">
              <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-emerald-700">Volá práve teraz</div>
              <Karta d={h} kompakt onOtvorEshop={(k) => setEshopKod(k)} onZavri={() => setHovory((x) => x.filter((y) => y.id !== h.id))} />
            </div>
          ))}
        </div>
      )}
      <KtoVolaOkno open={okno} onClose={() => setOkno(false)} user={user} predvolene={predvolene} onOtvorEshop={(k) => setEshopKod(k)} />
      {eshopKod && (
        <Suspense fallback={null}>
          <DetailObjednavkySklad kod={eshopKod} onClose={() => setEshopKod(null)} onZmena={() => {}} />
        </Suspense>
      )}
    </>
  );
}

